import { useAuthStore } from '@caradasstore/AuthStore'
import { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useCenterStore, useEntitlementStore, useLoadingStore, useMessageStore, useUserStore } from '../store'
import { BILLING_REFUSAL_CODES, billingRefusalMessage } from '../data/entitlements'
import { signOut } from '../utils/signOut'
import { refreshCenterDetail } from '../utils/refreshCenterDetail'
import { getFriendlyErrorMessage } from '../utils/errorMessage'
import { axiosSecure, refreshAuth } from './api'
import { vehicleInProgressFrom } from './vehicleInProgress'
import { REDIRECT_URLS } from '../constants'
import { Logger } from '../logger'

export const AuthFailureInterceptor = () => {
  const interceptorId = useRef(null)
  const authCtx = useAuthStore()
  const history = useHistory()
  const setLoading = useLoadingStore((store) => store.setLoading)
  const addMessage = useMessageStore((store) => store.addMessage)
  useEffect(() => {
    interceptorId.current = axiosSecure.interceptors.response.use(undefined, async (error) => {
      const originalRequest = error.config
      // A client user's write stopped by the request guard (api.js) never left the browser;
      // the control that sent it is hidden anyway, so fail quietly.
      if (error?.isReadOnlyBlocked) {
        setLoading(false)
        return Promise.reject(error)
      }
      // The backend's package gating (402/403 with a billing code, CAR-3710). The subscription
      // changed under the page - an expired trial, a failed renewal, a downgrade - so refresh
      // the entitlements (nav, routes and status bar catch up), explain once, and fail the call.
      // Never the forbidden page: the user may be exactly where they should be.
      const refusal = error.response?.data
      if (refusal && BILLING_REFUSAL_CODES.includes(refusal.code)) {
        useEntitlementStore.getState().loadEntitlements(useCenterStore.getState().ccId, { force: true })
        // An admin switched the feature off (CAR-4038): the switch lives on the center, not the plan.
        if (refusal.code === 'FEATURE_DISABLED') refreshCenterDetail()
        const refusedMethod = (originalRequest?.method || 'get').toLowerCase()
        const refusedWrite = !['get', 'head', 'options'].includes(refusedMethod)
        // A refused read is a lookup the page made on its own, not something the user did; the
        // hidden nav, the upgrade notice and the status bar already explain the plan. Only a
        // refused action gets a toast.
        if (refusedWrite && !originalRequest?.customErrorHandling) {
          addMessage({ text: billingRefusalMessage(refusal) })
        } else {
          Logger.info({
            message: 'Call refused by billing state',
            payload: {
              file: 'AuthFailureInterceptor',
              code: refusal.code,
              request: `${refusedMethod.toUpperCase()} ${originalRequest?.url}`
            }
          })
        }
        setLoading(false)
        return Promise.reject(error)
      }
      if (error.response?.status === 401) {
        // One refresh per request, ever. Without this, a request that keeps coming back
        // 401 - a genuinely forbidden call, or one the server rejects for a reason a new
        // token cannot fix - refreshes, retries, gets 401, refreshes again, and hammers
        // the API until the refresh eventually fails and the user is signed out.
        if (originalRequest?._hasRetriedAfterRefresh) {
          setLoading(false)
          return Promise.reject(error)
        }
        if (originalRequest) {
          originalRequest._hasRetriedAfterRefresh = true
        }
        try {
          // refreshAxios uses different axios instance
          const authRefreshResponse = await refreshAuth({})
          if (authRefreshResponse.status === 200) {
            const { token, refreshToken } = authRefreshResponse.data
            authCtx.login(token, refreshToken)
            originalRequest.headers.Authorization = `Bearer ${token}`
            return axiosSecure(originalRequest)
          }
          throw new Error('unable to refresh token')
        } catch (error) {
          // The session could not be renewed: the shared sign-out (CAR-4086) ends it cleanly.
          signOut(history)
        }
      } else if (error.response?.status === 403) {
        // A refused action must not cost the user the page they are on. The forbidden
        // page means "you may not be here", which is the right answer for a GET the
        // user is not allowed to make; it is the wrong answer for a write that was
        // declined, and for a read-only user it would fire on every attempted action.
        const requestMethod = (originalRequest?.method || 'get').toLowerCase()
        const isWrite = !['get', 'head', 'options'].includes(requestMethod)
        const isReadOnlyUser = !!useUserStore.getState().currentUser?.is_client_user

        // A client user's refused read is something the page asked for in the background,
        // not something the user did - a toast each time would repeat on every page and
        // poll. Log it (so a missing allowlist entry can be found) and fail quietly.
        if (isReadOnlyUser && !isWrite) {
          Logger.info({
            message: 'Read refused for a client user',
            payload: { file: 'AuthFailureInterceptor', request: `${requestMethod.toUpperCase()} ${originalRequest?.url}` }
          })
          setLoading(false)
          return Promise.reject(error)
        }

        if (isWrite || isReadOnlyUser) {
          if (!originalRequest?.customErrorHandling) {
            addMessage({ text: isReadOnlyUser ? 'Your account has read-only access.' : getFriendlyErrorMessage(error) })
          }
          setLoading(false)
          return Promise.reject(error)
        }

        history.push(REDIRECT_URLS.FORBIDDEN_PAGE)
      } else if (originalRequest?.customErrorHandling || (originalRequest?.handlesVehicleInProgress && vehicleInProgressFrom(error))) {
        // handlesVehicleInProgress: the page turns a job-in-progress 409 into its own confirm (CAR-4021);
        // every other error from that call is still toasted here.
        setLoading(false)
        return Promise.reject(error)
      } else {
        // CAR-3634: show one friendly, mapped message (never raw text) and re-throw.
        // Previously this branch resolved `undefined`, so callers crashed reading `.data`.
        addMessage({ text: getFriendlyErrorMessage(error) })
        setLoading(false)
        return Promise.reject(error)
      }
    })

    return () => {
      // Eject from the instance it was registered on. This said `axios`, so every
      // re-run of this effect left the previous handler in place and a single 401
      // fanned out into one refresh attempt per stacked copy.
      axiosSecure.interceptors.response.eject(interceptorId.current)
    }
  }, [addMessage, authCtx, history, setLoading])

  return null
}
